import {TasksFiltersAction, FiltersActionType, DailyCareLogFiltersState} from './types';

const toggleActivitiesGroupFilters = (
  prevState: DailyCareLogFiltersState,
  selected: boolean,
  ...activitiesGroupIds: string[]
): DailyCareLogFiltersState => {
  const prevActivitiesGroupIds = prevState.taskFilters.activitiesGroupIds || [];
  let updatedActivitiesGroupIds: string[];

  if (selected) {
    updatedActivitiesGroupIds = [...prevActivitiesGroupIds, ...activitiesGroupIds];
  } else {
    updatedActivitiesGroupIds = prevActivitiesGroupIds.filter(
      (id) => !activitiesGroupIds.includes(id)
    );
  }

  return {
    ...prevState,
    taskFilters: {
      ...prevState.taskFilters,
      activitiesGroupIds: updatedActivitiesGroupIds,
    },
  };
};

const toggleSocialResponseFilter = (
  prevState: DailyCareLogFiltersState,
  socialResponseId: string,
  selected: boolean
): DailyCareLogFiltersState => {
  const prevSocialResponseIds = prevState.taskFilters.socialResponseIds;
  let updatedSocialResponseIds: string[];

  if (selected) {
    updatedSocialResponseIds = [...prevSocialResponseIds, socialResponseId];
  } else {
    updatedSocialResponseIds = prevSocialResponseIds.filter((id) => id !== socialResponseId);
  }

  return {
    ...prevState,
    taskFilters: {
      ...prevState.taskFilters,
      socialResponseIds: updatedSocialResponseIds,
    },
  };
};

const reducer = (
  prevState: DailyCareLogFiltersState,
  action: TasksFiltersAction
): DailyCareLogFiltersState => {
  switch (action.type) {
    case FiltersActionType.SET_ACTIVITIES_SECTORS:
      return {
        ...prevState,
        activitiesSectors: action.activitiesSectors,
      };

    case FiltersActionType.SET_ACTIVITIES_SECTOR_GROUPS:
      return {
        ...prevState,
        activityGroups: {
          ...prevState.activityGroups,
          [action.activitiesSectorId]: action.activitiesGroups,
        },
      };

    case FiltersActionType.SET_TASKS_FILTERS:
      return {
        ...prevState,
        taskFilters: {
          ...prevState.taskFilters,
          activitiesGroupIds: action.tasksFilters.activitiesGroupIds
            ? [...action.tasksFilters.activitiesGroupIds]
            : [],
          socialResponseIds: [...action.tasksFilters.socialResponseIds],
        },
      };

    case FiltersActionType.TOGGLE_ACTIVITIES_SECTOR_FILTER: {
      const sectorGroupIds: string[] =
        prevState.activityGroups?.[action.activitiesSectorId]?.map(
          (activitiesGroup) => activitiesGroup.id!
        ) ?? [];

      return toggleActivitiesGroupFilters(prevState, action.selected, ...sectorGroupIds);
    }

    case FiltersActionType.TOGGLE_ACTIVITIES_GROUP_FILTER:
      return toggleActivitiesGroupFilters(prevState, action.selected, action.activitiesGroupId);

    case FiltersActionType.TOGGLE_SOCIAL_RESPONSE_FILTER:
      return toggleSocialResponseFilter(prevState, action.socialResponseId, action.selected);

    case FiltersActionType.TOGGLE_ALL_SOCIAL_RESPONSE_FILTERS:
      return {
        ...prevState,
        taskFilters: {
          ...prevState.taskFilters,
          socialResponseIds: action.selected
            ? (action.socialResponses?.map((socialResponse) => socialResponse.id) ?? [])
            : [],
        },
      };
    case FiltersActionType.TOGGLE_ALL_STATUS_FILTERS:
      return {
        ...prevState,
        taskFilters: {
          ...prevState.taskFilters,
          status: action.status,
        },
      };

    case FiltersActionType.TOGGLE_ALL_USER_FILTERS:
      return {
        ...prevState,
        taskFilters: {
          ...prevState.taskFilters,
          users: action.users,
        },
      };
    case FiltersActionType.TOGGLE_STATUS_FILTER:
      return {
        ...prevState,
        taskFilters: {
          ...prevState.taskFilters,
          status: !prevState.taskFilters.status
            ? []
            : action.selected
              ? [...prevState.taskFilters.status, action.status]
              : prevState.taskFilters.status.filter((type) => type !== action.status),
        },
      };
    case FiltersActionType.TOGGLE_USER_FILTER:
      return {
        ...prevState,
        taskFilters: {
          ...prevState.taskFilters,
          users: !prevState.taskFilters.users
            ? []
            : action.selected
              ? [...prevState.taskFilters.users, action.userId]
              : prevState.taskFilters.users.filter((type) => type !== action.userId),
        },
      };

    case FiltersActionType.TOGGLE_OCCURRENCE_TYPE_GENERAL_FILTER:
      return {
        ...prevState,
        taskFilters: {
          ...prevState.taskFilters,
          occurrencesTypesGeneral: !prevState.taskFilters.occurrencesTypesGeneral
            ? []
            : action.selected
              ? [...prevState.taskFilters.occurrencesTypesGeneral, action.occurrenceType]
              : prevState.taskFilters.occurrencesTypesGeneral.filter(
                  (type) => type !== action.occurrenceType
                ),
        },
      };
    case FiltersActionType.TOGGLE_OCCURRENCE_TYPE_PATIENTS_FILTER:
      return {
        ...prevState,
        taskFilters: {
          ...prevState.taskFilters,
          occurrencesTypesPatients: !prevState.taskFilters.occurrencesTypesPatients
            ? []
            : action.selected
              ? [...prevState.taskFilters.occurrencesTypesPatients, action.occurrenceType]
              : prevState.taskFilters.occurrencesTypesPatients.filter(
                  (type) => type !== action.occurrenceType
                ),
        },
      };
    case FiltersActionType.TOGGLE_ALL_OCCURRENCE_TYPE_GENERAL_FILTERS:
      return {
        ...prevState,
        taskFilters: {
          ...prevState.taskFilters,
          occurrencesTypesGeneral: action.selected ? action.occurrenceTypes : [],
        },
      };
    case FiltersActionType.TOGGLE_ALL_OCCURRENCE_TYPE_PATIENTS_FILTERS:
      return {
        ...prevState,
        taskFilters: {
          ...prevState.taskFilters,
          occurrencesTypesPatients: action.selected ? action.occurrenceTypes : [],
        },
      };
    case FiltersActionType.TOGGLE_ATTENDANCE_FILTER:
      return {
        ...prevState,
        taskFilters: {
          ...prevState.taskFilters,
          [action.group]: action.selected
            ? [...prevState.taskFilters[action.group], action.value]
            : prevState.taskFilters[action.group].filter((value) => value !== action.value),
        },
      };
    case FiltersActionType.TOGGLE_ALL_ATTENDANCE_FILTERS:
      return {
        ...prevState,
        taskFilters: {
          ...prevState.taskFilters,
          [action.group]: action.values,
        },
      };
    case FiltersActionType.CLEAR_ALL_FILTERS:
      return {
        ...prevState,
        taskFilters: {
          activitiesGroupIds: [],
          socialResponseIds: [],
          users: [],
          status: [],
          occurrencesTypesGeneral: [],
          occurrencesTypesPatients: [],
          attendanceMovements: [],
          attendanceStatus: [],
          attendancePeriods: [],
        },
      };
  }
};

export default reducer;
